/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useEffect, useState } from "react";
import Loading from "./Loading";
import { LayoutContext } from "../components/layout";
import fetchHelper from "../services/fetchHelper";
import "./Esign.css";
import { AssignPhysician } from "./Esign/AssignPhysician";
import { Notes } from "./Esign/Notes";
import { CPTCodes } from "./Esign/CPTCodes";
import { DiagnosisCodes } from "./Esign/DiagnosisCodes";
import { SaveSign } from "./Esign/SaveSign";
import { Statements } from "./Esign/Statements";
import { useToasts } from "react-toast-notifications";

const Esign = () => {
  const {
    headerDetails: userInfo,
    readPhysicians,
    getPatientData,
    loadAssignerInfo,
  } = useContext(LayoutContext);
  const [loadLoading, setLoadLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [showCPT, setShowCPT] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [nurseFollowUp, setNurseFollowUp] = useState(null);
  const [nurseFollowUpLoading, setNurseFollowUpLoading] = useState(false);
  const [seed, setSeed] = useState(1);
  const { addToast } = useToasts();
  let prevReportStatements = [...userInfo.reportStatements];

  useEffect(() => {
    if (userInfo) {
      setNurseFollowUp(userInfo.nurseFollowUp);
      setLoadLoading(false);
    }
  }, [userInfo]);

  const onFollowUpChange = async () => {
    try {
      setNurseFollowUpLoading(true);
      let url = `/patientmanagement/rest/interrogations/${userInfo.id}`;
      await fetchHelper.put(url, {
        nurseFollowUp: !nurseFollowUp,
        id: userInfo.id,
      });
    } catch ({ errors }) {
      errors &&
        errors.forEach((err) => {
          addToast(err.msg, {
            appearance: "error",
            autoDismiss: false,
          });
        });
      setNurseFollowUp(nurseFollowUp);
    } finally {
      setNurseFollowUpLoading(false);
      loadAssignerInfo(userInfo.id);
    }
  };

  const updatePrevReportStatements = (items, newItem = {}) => {
    if (newItem.cstId) {
      prevReportStatements.push(newItem);
    } else {
      prevReportStatements = prevReportStatements.filter((item) =>
        items.some((val) => val.cstId === item.cstId)
      );
    }
  };

  const showAll = async () => {
    if (prevReportStatements.length !== userInfo.reportStatements.length) {
      const result = await getPatientData(userInfo.id);
      userInfo.reportStatements = result.reportStatements;
    }
    setShow(!show);
  };

  const reloadComponent = () => {
    setSeed(Math.random());
  };

  if (loadLoading) {
    return (
      <>
        <Loading />
        <Loading />
        <Loading />
        <Loading />
      </>
    );
  }

  return (
    <>
      <div id="esign-container">
        <div className="sb-content e-notes">
          <div className="grp">
            <form className="form-esign">
              <Statements
                showAll={showAll}
                show={show}
                setShow={setShow}
                updatePrevReportStatements={updatePrevReportStatements}
              />
              <Notes />
              <CPTCodes
                key={seed}
                suggestions={suggestions}
                setSuggestions={setSuggestions}
                showCPT={showCPT}
                setShowCPT={setShowCPT}
              />
              <DiagnosisCodes showCPT={showCPT} />
              <AssignPhysician
                readPhysicians={readPhysicians}
                loadAssignerInfo={loadAssignerInfo}
                reloadComponent={reloadComponent}
              />
            </form>
          </div>
        </div>
        <SaveSign
          onFollowUpChange={onFollowUpChange}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          nurseFollowUp={nurseFollowUp}
          nurseFollowUpLoading={nurseFollowUpLoading}
          setNurseFollowUp={setNurseFollowUp}
          loadAssignerInfo={loadAssignerInfo}
        />
      </div>
    </>
  );
};

export default Esign;
